/**
 * InventorySettingsPage — הגדרות מלאי
 *
 * Two-tier navigation layout:
 *   Vertical sidebar (right) lists main categories.
 *   Horizontal sub-tab bar (top of content area) lists sub-sections
 *   within each category (only rendered when the category has > 1 sub-tab).
 *   Content pane (left) renders the active section.
 *
 * Vertical sidebar categories (in order):
 *   מוצרים ומלאי  → sub-tabs: קבוצות, מחירונים, וריאציות, מק״ט
 *   לקוחות        → sub-tabs: ניהול לקוחות
 *   ספקים         → single view (no sub-tabs shown)
 *   סוכנים        → single view (AgentsSettingsView CRUD)
 *   מחסנים        → full CRUD (WarehousesSettingsView)
 *   הגדרות חברה   → single view (CompanySettingsView – VAT rate etc.)
 *
 * Route: /inventory/settings
 * Guard: module="inventory" level="editor"
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Users, Plus, Pencil, Trash2, X, Loader as Loader2, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Store, Mail, Phone, SendHorizontal as SendHorizonal, FileSliders as Sliders, FolderTree, Tags, ChevronDown, Receipt, Package, Truck, Barcode, ChevronLeft, Briefcase, Warehouse, ToggleLeft, ToggleRight, Building2, FileText, ReceiptText, Search } from 'lucide-react';
import { supabase }              from '@/lib/supabase';
import { useCompany }            from '@/context/CompanyContext';
import { ProductGroupsSettings } from './ProductGroupsSettings';
import { PricelistsSettings }    from './PricelistsSettings';
import { SkuSettingsTab }        from './SkuSettingsTab';
import { AgentsSettingsView,    type InventoryAgent }       from './AgentsSettingsView';
import { WarehousesSettingsView, type InventoryWarehouse }  from './WarehousesSettingsView';
import { SuppliersSettingsView }                            from './SuppliersSettingsView';
import { CompanySettingsView, fetchCompanyConsignmentEnabled } from './CompanySettingsView';
import { DocumentTypesSettingsView }                         from './DocumentTypesSettingsView';
import { B2BClientTermsPanel, type B2BClientTermsPanelHandle } from '@/components/inventory/B2BClientTermsPanel';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Pricelist {
  id:   string;
  name: string;
}

interface InventoryClient {
  id:                        string;
  company_id:                string;
  name:                      string;
  email?:                    string | null;
  phone?:                    string | null;
  pricelist_id:              string | null;
  agent_id:                  string | null;
  consignment_warehouse_id:  string | null;
  is_vat_liable:             boolean;
  /** Phase 34: unique business registration number (ח.פ. / עוסק מורשה) */
  business_id?:              string | null;
  rivhit_customer_id?:       number | null;
  /** Phase 34: allow client to access B2B ordering portal */
  b2b_allowed?:              boolean;
  /** Phase 109: 'none' | 'view_only' | 'full' */
  client_access?:            string;
  /** Phase 72: whether this client may purchase via consignment.
   *  UI enforces a strict interlock — cannot be true unless
   *  consignment_warehouse_id is non-null. */
  allow_consignment?:        boolean;
  allow_standard_sales?:     boolean;
  created_at:                string;
  pricelist_name?:           string | null;
  agent_name?:               string | null;
  consignment_warehouse_name?: string | null;
}

interface InventoryVariationType {
  id:         string;
  company_id: string;
  name:       string;
  created_at: string;
  updated_at: string;
}

interface Toast { msg: string; ok: boolean }

// ─── Navigation structure ─────────────────────────────────────────────────────

// Each top-level category IS its own view; sub-tabs are optional sections
// within that category. Categories with a single sub-tab hide the sub-tab bar.
type MainCatId = 'products' | 'clients' | 'suppliers' | 'agents' | 'warehouses' | 'company' | 'documents';
type SubTabId  =
  | 'groups' | 'pricelists' | 'variations' | 'sku'   // products
  | 'clients'                                          // clients (agents removed)
  | 'suppliers'                                        // suppliers
  | 'agents'                                           // top-level agents view
  | 'warehouses'                                       // top-level warehouses view
  | 'company'                                          // company settings (VAT etc.)
  | 'documents';                                       // document types (Phase 17)

interface SubTab {
  id:    SubTabId;
  label: string;
  icon:  React.ReactNode;
}

interface MainCategory {
  id:      MainCatId;
  label:   string;
  icon:    React.ReactNode;
  subTabs: SubTab[];
}

const NAV: MainCategory[] = [
  {
    id:    'products',
    label: 'מוצרים ומלאי',
    icon:  <Package className="w-4 h-4" />,
    subTabs: [
      { id: 'groups',     label: 'קבוצות מוצרים', icon: <FolderTree className="w-3.5 h-3.5" /> },
      { id: 'pricelists', label: 'מחירונים',       icon: <Tags       className="w-3.5 h-3.5" /> },
      { id: 'variations', label: 'וריאציות',       icon: <Sliders    className="w-3.5 h-3.5" /> },
      { id: 'sku',        label: 'הגדרות מק״ט',    icon: <Barcode    className="w-3.5 h-3.5" /> },
    ],
  },
  {
    id:    'clients',
    label: 'לקוחות',
    icon:  <Users className="w-4 h-4" />,
    subTabs: [
      // 'agents' removed from here — now a top-level sidebar category
      { id: 'clients', label: 'ניהול לקוחות', icon: <Store className="w-3.5 h-3.5" /> },
    ],
  },
  {
    id:    'suppliers',
    label: 'ספקים',
    icon:  <Truck className="w-4 h-4" />,
    subTabs: [
      { id: 'suppliers', label: 'ניהול ספקים', icon: <Truck className="w-3.5 h-3.5" /> },
    ],
  },
  {
    id:    'agents',
    label: 'סוכנים',
    icon:  <Briefcase className="w-4 h-4" />,
    subTabs: [
      { id: 'agents', label: 'ניהול סוכנים', icon: <Briefcase className="w-3.5 h-3.5" /> },
    ],
  },
  {
    id:    'warehouses',
    label: 'מחסנים',
    icon:  <Warehouse className="w-4 h-4" />,
    subTabs: [
      { id: 'warehouses', label: 'מחסנים', icon: <Warehouse className="w-3.5 h-3.5" /> },
    ],
  },
  {
    id:    'company',
    label: 'הגדרות חברה',
    icon:  <Building2 className="w-4 h-4" />,
    subTabs: [
      { id: 'company', label: 'הגדרות חברה', icon: <Building2 className="w-3.5 h-3.5" /> },
    ],
  },
  {
    id:    'documents',
    label: 'הגדרות מסמכים',
    icon:  <FileText className="w-4 h-4" />,
    subTabs: [
      { id: 'documents', label: 'סוגי מסמכים', icon: <FileText className="w-3.5 h-3.5" /> },
    ],
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function InventorySettingsPage() {
  const { activeCompanyId } = useCompany();

  // ── Navigation state ──────────────────────────────────────────────────────
  const [activeMain, setActiveMain] = useState<MainCatId>('products');
  const [activeSub,  setActiveSub]  = useState<SubTabId>('groups');

  // Single-view categories show no sub-tab bar (only one sub-tab)
  const isSingleView = (
    activeMain === 'agents'     ||
    activeMain === 'warehouses' ||
    activeMain === 'suppliers'  ||
    activeMain === 'company'    ||
    activeMain === 'documents'
  );

  const switchMain = (cat: MainCategory) => {
    setActiveMain(cat.id);
    const firstSub = cat.subTabs[0];
    if (firstSub) setActiveSub(firstSub.id);   // always land on the first sub-tab
  };

  // NAV is a non-empty const array so this is always defined;
  // cast to remove TS strict-undefined on Array.find
  const currentCat = (NAV.find(c => c.id === activeMain) ?? NAV[0]) as MainCategory;

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<Toast | null>(null);
  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ──────────────────────────────────────────────────────────────────────────
  // CLIENT MANAGEMENT state + logic
  // ──────────────────────────────────────────────────────────────────────────
  const [clients,         setClients]         = useState<InventoryClient[]>([]);
  const [custLoading,     setCustLoading]     = useState(true);
  const [custError,       setCustError]       = useState<string | null>(null);
  const [custModalOpen,   setCustModalOpen]   = useState(false);
  const [custEditing,     setCustEditing]     = useState<InventoryClient | null>(null);
  const [custName,        setCustName]        = useState('');
  const [custEmail,       setCustEmail]       = useState('');
  const [custPhone,       setCustPhone]       = useState('');
  const [custPricelistId,         setCustPricelistId]         = useState<string | null>(null);
  const [custAgentId,             setCustAgentId]             = useState<string | null>(null);
  const [custConsignmentWHId,     setCustConsignmentWHId]     = useState<string | null>(null);
  const [custIsVatLiable,         setCustIsVatLiable]         = useState<boolean>(true);
  const [custBusinessId,          setCustBusinessId]          = useState<string>('');
  const [custRivhitId,            setCustRivhitId]            = useState<string>('');
  const [custB2bAllowed,          setCustB2bAllowed]          = useState<boolean>(false);
  const [custClientAccess,        setCustClientAccess]        = useState<string>('none');
  const [custAllowConsignment,    setCustAllowConsignment]    = useState<boolean>(false);
  const [custAllowStandardSales,  setCustAllowStandardSales]  = useState<boolean>(true);
  const [custConsignmentWarning,  setCustConsignmentWarning]  = useState<string | null>(null);
  const [custSaving,              setCustSaving]              = useState(false);
  const [custFormError,           setCustFormError]           = useState<string | null>(null);
  const [custDelTarget,           setCustDelTarget]           = useState<InventoryClient | null>(null);
  const [custDeleting,            setCustDeleting]            = useState(false);
  const [custModalTab,            setCustModalTab]            = useState<'general' | 'extra' | 'terms'>('general');
  const custTermsRef = useRef<B2BClientTermsPanelHandle>(null);

  const [pricelists,          setPricelists]          = useState<Pricelist[]>([]);
  const [agents,              setAgents]              = useState<InventoryAgent[]>([]);
  const [consignmentWarehouses, setConsignmentWarehouses] = useState<InventoryWarehouse[]>([]);

  // Company-level consignment flag (Phase 78)
  const [companyConsignmentEnabled, setCompanyConsignmentEnabled] = useState(false);

  const fetchClients = useCallback(async () => {
    if (!activeCompanyId) return;
    setCustLoading(true); setCustError(null);
    const { data, error } = await supabase
      .from('inventory_clients')
      .select(`
        id, company_id, name, email, phone, pricelist_id, agent_id,
        consignment_warehouse_id, allow_consignment, allow_standard_sales, is_vat_liable, business_id, rivhit_customer_id, b2b_allowed, client_access, created_at,
        inventory_pricelists ( name ),
        agent:inventory_agents!inventory_clients_agent_id_fkey ( name ),
        inventory_warehouses!fk_client_consignment_warehouse ( name )
      `)
      .eq('company_id', activeCompanyId)
      .order('name');
    if (error) { setCustError(error.message); setCustLoading(false); return; }
    setClients((data ?? []).map((r: any) => ({
      id:                        r.id,
      company_id:                r.company_id,
      name:                      r.name,
      email:                     r.email,
      phone:                     r.phone,
      pricelist_id:              r.pricelist_id,
      agent_id:                  r.agent_id                  ?? null,
      consignment_warehouse_id:  r.consignment_warehouse_id  ?? null,
      is_vat_liable:             r.is_vat_liable              ?? true,
      business_id:               r.business_id               ?? null,
      rivhit_customer_id:        r.rivhit_customer_id        ?? null,
      b2b_allowed:               r.b2b_allowed               ?? false,
      client_access:             r.client_access             ?? 'none',
      allow_consignment:         r.allow_consignment          ?? false,
      allow_standard_sales:      r.allow_standard_sales       ?? true,
      created_at:                r.created_at,
      pricelist_name:            r.inventory_pricelists?.name                   ?? null,
      agent_name:                r.agent?.name                                  ?? null,
      consignment_warehouse_name: r.inventory_warehouses?.name ?? null,
    })));
    setCustLoading(false);
  }, [activeCompanyId]);

  const fetchPricelists = useCallback(async () => {
    if (!activeCompanyId) return;
    const { data } = await supabase
      .from('inventory_pricelists')
      .select('id, name')
      .eq('company_id', activeCompanyId)
      .order('name');
    setPricelists(data ?? []);
  }, [activeCompanyId]);

  const fetchAgentsList = useCallback(async () => {
    if (!activeCompanyId) return;
    const { data } = await supabase
      .from('inventory_agents')
      .select('id, company_id, name, phone, email, sales_commission_rate, collection_commission_rate, notes, created_at, updated_at')
      .eq('company_id', activeCompanyId)
      .order('name');
    setAgents((data as InventoryAgent[]) ?? []);
  }, [activeCompanyId]);

  const fetchConsignmentWarehouses = useCallback(async () => {
    if (!activeCompanyId) return;
    const { data } = await supabase
      .from('inventory_warehouses')
      .select('id, company_id, warehouse_number, name, is_consignment, created_at, updated_at')
      .eq('company_id', activeCompanyId)
      .eq('is_consignment', true)
      .order('name');
    setConsignmentWarehouses((data as InventoryWarehouse[]) ?? []);
  }, [activeCompanyId]);

  useEffect(() => {
    if (activeSub === 'clients') {
      fetchClients(); fetchPricelists(); fetchAgentsList(); fetchConsignmentWarehouses();
    }
  }, [activeSub, fetchClients, fetchPricelists, fetchAgentsList, fetchConsignmentWarehouses]);

  // Fetch company consignment flag whenever company changes (Phase 78)
  useEffect(() => {
    if (!activeCompanyId) return;
    fetchCompanyConsignmentEnabled(activeCompanyId).then(setCompanyConsignmentEnabled);
  }, [activeCompanyId]);

  const openCustAdd = () => {
    setCustEditing(null); setCustName(''); setCustEmail(''); setCustPhone('');
    setCustPricelistId(null); setCustAgentId(null);
    setCustConsignmentWHId(null); setCustIsVatLiable(true);
    setCustBusinessId(''); setCustRivhitId(''); setCustB2bAllowed(false); setCustClientAccess('none');
    setCustAllowConsignment(false); setCustAllowStandardSales(true); setCustConsignmentWarning(null);
    setCustFormError(null); setCustModalTab('general'); setCustModalOpen(true);
  };
  const openCustEdit = (c: InventoryClient) => {
    setCustEditing(c); setCustName(c.name); setCustEmail(c.email ?? '');
    setCustPhone(c.phone ?? '');
    setCustPricelistId(c.pricelist_id);
    setCustAgentId(c.agent_id ?? null);
    setCustConsignmentWHId(c.consignment_warehouse_id ?? null);
    setCustIsVatLiable(c.is_vat_liable ?? true);
    setCustBusinessId(c.business_id ?? '');
    setCustRivhitId(c.rivhit_customer_id ? String(c.rivhit_customer_id) : '');
    setCustB2bAllowed(c.b2b_allowed ?? false);
    setCustClientAccess(c.client_access ?? 'none');
    // Phase 72 strict interlock: if the persisted state somehow has
    // allow_consignment=true with no warehouse, force-OFF for safety.
    setCustAllowConsignment(
      (c.allow_consignment ?? false) && !!c.consignment_warehouse_id,
    );
    setCustAllowStandardSales(c.allow_standard_sales ?? true);
    setCustConsignmentWarning(null);
    setCustFormError(null); setCustModalTab('general'); setCustModalOpen(true);
  };
  const closeCustModal = () => {
    setCustModalOpen(false); setCustEditing(null);
    setCustName(''); setCustEmail(''); setCustPhone('');
    setCustPricelistId(null); setCustAgentId(null);
    setCustConsignmentWHId(null); setCustIsVatLiable(true);
    setCustBusinessId(''); setCustRivhitId(''); setCustB2bAllowed(false); setCustClientAccess('none');
    setCustAllowConsignment(false); setCustAllowStandardSales(true); setCustConsignmentWarning(null);
    setCustFormError(null); setCustModalTab('general');
  };
  const requestCloseCustModal = () => {
    if (custTermsRef.current?.isDirty()
        && !window.confirm('יש תנאים שהקלדת ולא נשמרו. לסגור בלי לשמור?')) return;
    closeCustModal();
  };

  const validateCust = () => {
    if (!custName.trim()) return 'שם הלקוח הוא שדה חובה';
    const em  = custEmail.trim();
    const ph  = custPhone.trim();
    if (!em && !ph) return 'יש למלא לפחות אימייל או טלפון לצורך זיהוי בסיסמה חד-פעמית (OTP)';
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return 'כתובת אימייל אינה תקינה';
    if (ph && !/^[\d\s\-\+\(\)]{7,15}$/.test(ph)) return 'מספר טלפון אינו תקין';
    // Phase 72: when B2B portal access is enabled, phone is strictly required
    // (the B2B portal flow relies on phone-based OTP for client login).
    if (custB2bAllowed && !ph) {
      return 'יש להזין מספר טלפון כדי לאפשר גישה לפורטל B2B.';
    }
    // Phase 72: defensive — UI prevents this state, but block save if reached.
    if (custAllowConsignment && !custConsignmentWHId) {
      return 'לא ניתן לאפשר קונסגנציה. יש להגדיר תחילה מחסן קונסגנציה עבור הלקוח בניהול מחסנים, לבחור אותו כאן ברשימה, ורק אז לאפשר מכירה.';
    }
    return null;
  };

  // Phase 72: try to enable the "Allow Consignment Sales" toggle.
  // Strict interlock — prevented entirely if no warehouse is selected.
  const tryToggleAllowConsignment = () => {
    if (custAllowConsignment) {
      // Turning OFF is always permitted, and clears any prior warning.
      setCustAllowConsignment(false);
      setCustConsignmentWarning(null);
      return;
    }
    // Attempting to turn ON
    if (!custConsignmentWHId) {
      setCustConsignmentWarning(
        'לא ניתן לאפשר קונסגנציה. יש להגדיר תחילה מחסן קונסגנציה עבור הלקוח בניהול מחסנים, לבחור אותו כאן ברשימה, ורק אז לאפשר מכירה.',
      );
      return;
    }
    setCustAllowConsignment(true);
    setCustConsignmentWarning(null);
  };

  // Phase 72: when the warehouse is cleared, auto-disable the consignment
  // toggle so the persisted state can never violate the interlock.
  const handleConsignmentWHChange = (newId: string | null) => {
    setCustConsignmentWHId(newId);
    if (!newId && custAllowConsignment) {
      setCustAllowConsignment(false);
      setCustConsignmentWarning(
        'מחסן הקונסגנציה הוסר — מכירה בקונסגנציה כובתה אוטומטית.',
      );
    } else if (newId) {
      // Clearing a stale interlock warning once a warehouse is picked.
      setCustConsignmentWarning(null);
    }
  };

  const handleCustSave = async () => {
    const err = validateCust();
    if (err) { setCustFormError(err); return; }
    const rivhitRaw = custRivhitId.trim();
    if (rivhitRaw && !/^[1-9]\d{0,8}$/.test(rivhitRaw)) { setCustModalTab('extra'); setCustFormError('מספר הלקוח ברווחית חייב להכיל ספרות בלבד'); return; }
    const rivhit_customer_id      = rivhitRaw ? Number(rivhitRaw) : null;
    const name                    = custName.trim();
    const email                   = custEmail.trim()  || null;
    const phone                   = custPhone.trim()  || null;
    const pricelist_id            = custPricelistId   || null;
    const agent_id                = custAgentId       || null;
    const consignment_warehouse_id = custConsignmentWHId || null;
    const is_vat_liable           = custIsVatLiable;
    const business_id             = custBusinessId.trim() || null;
    const b2b_allowed             = custB2bAllowed;
    const client_access           = b2b_allowed ? custClientAccess : 'none';
    setCustSaving(true); setCustFormError(null);
    try {
      const termsChanged = Boolean(custEditing && custTermsRef.current?.isDirty());
      if (termsChanged && custTermsRef.current) {
        const res = await custTermsRef.current.save();
        if (!res.ok) {
          setCustModalTab('terms');
          setCustFormError(`שמירת התנאים נכשלה: ${res.message}`);
          return;
        }
      }
      // Phase 72 — defensive normalisation of the consignment interlock at
      // persistence time: allow_consignment can only be true when a warehouse
      // is actually assigned. The UI enforces this, but we double-check here
      // so the DB never holds an inconsistent state.
      const allow_consignment = !!consignment_warehouse_id && custAllowConsignment;
      const allow_standard_sales = custAllowStandardSales;
      if (custEditing) {
        const { error } = await supabase.from('inventory_clients')
          .update({ name, email, phone, pricelist_id, agent_id, consignment_warehouse_id, is_vat_liable, business_id, rivhit_customer_id, b2b_allowed, client_access, allow_consignment, allow_standard_sales })
          .eq('id', custEditing.id);
        if (error) throw error;
        showToast(termsChanged ? 'הלקוח והתנאים שלו עודכנו בהצלחה' : 'לקוח עודכן בהצלחה');
      } else {
        const { error } = await supabase.from('inventory_clients')
          .insert({ company_id: activeCompanyId, name, email, phone, pricelist_id, agent_id, consignment_warehouse_id, is_vat_liable, business_id, rivhit_customer_id, b2b_allowed, client_access, allow_consignment, allow_standard_sales });
        if (error) throw error;
        showToast('לקוח נוסף בהצלחה');
      }
      // Ensure a Supabase Auth user exists for B2B portal access
      if (b2b_allowed && client_access !== 'none' && (email || phone)) {
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-b2b-auth-user`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ email, phone, name }),
          });
        } catch { /* non-fatal — WhatsApp fallback still works */ }
      }
      closeCustModal(); fetchClients();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'שגיאה בשמירה', false);
    } finally {
      setCustSaving(false);
    }
  };

  // ── Invite client via WhatsApp (phone) or mailto (email fallback) ─────────
  const handleInviteClient = useCallback((c: InventoryClient) => {
    const b2bUrl = `${window.location.origin}/b2b`;
    const message = `היי ${c.name}, מצורף קישור למערכת ההזמנות שלנו. לכניסה: ${b2bUrl}`;
    const encoded = encodeURIComponent(message);

    if (c.phone) {
      // Normalise: keep only digits and leading +, strip spaces/dashes/parentheses
      const digits = c.phone.replace(/[^\d+]/g, '');
      // If number starts with 0 (local Israeli), convert to +972
      const intl = digits.startsWith('0') ? `+972${digits.slice(1)}` : digits;
      window.open(`https://wa.me/${intl}?text=${encoded}`, '_blank', 'noopener,noreferrer');
    } else if (c.email) {
      const subject = encodeURIComponent('הזמנה למערכת ההזמנות');
      window.location.href = `mailto:${c.email}?subject=${subject}&body=${encoded}`;
    } else {
      showToast('יש להוסיף טלפון או אימייל כדי לשלוח הזמנה', false);
    }
  }, [showToast]);

  const handleCustDelete = async () => {
    if (!custDelTarget) return;
    setCustDeleting(true);
    const { error } = await supabase.from('inventory_clients').delete().eq('id', custDelTarget.id);
    if (error) { showToast(error.message, false); }
    else       { showToast('לקוח נמחק'); setCustDelTarget(null); fetchClients(); }
    setCustDeleting(false);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // VARIATION TYPES state + logic
  // ──────────────────────────────────────────────────────────────────────────
  const [variations,   setVariations]   = useState<InventoryVariationType[]>([]);
  const [varLoading,   setVarLoading]   = useState(true);
  const [varError,     setVarError]     = useState<string | null>(null);
  const [varModalOpen, setVarModalOpen] = useState(false);
  const [varEditing,   setVarEditing]   = useState<InventoryVariationType | null>(null);
  const [varName,      setVarName]      = useState('');
  const [varSaving,    setVarSaving]    = useState(false);
  const [varFormError, setVarFormError] = useState<string | null>(null);
  const [varDelTarget, setVarDelTarget] = useState<InventoryVariationType | null>(null);
  const [varDeleting,  setVarDeleting]  = useState(false);

  const fetchVariations = useCallback(async () => {
    if (!activeCompanyId) return;
    setVarLoading(true); setVarError(null);
    const { data, error } = await supabase
      .from('inventory_variation_types')
      .select('id, company_id, name, created_at, updated_at')
      .eq('company_id', activeCompanyId)
      .order('name');
    if (error) setVarError(error.message);
    else       setVariations(data ?? []);
    setVarLoading(false);
  }, [activeCompanyId]);

  useEffect(() => {
    if (activeSub === 'variations') fetchVariations();
  }, [activeSub, fetchVariations]);

  const openVarAdd = () => {
    setVarEditing(null); setVarName(''); setVarFormError(null); setVarModalOpen(true);
  };
  const openVarEdit = (v: InventoryVariationType) => {
    setVarEditing(v); setVarName(v.name); setVarFormError(null); setVarModalOpen(true);
  };
  const closeVarModal = () => {
    setVarModalOpen(false); setVarEditing(null); setVarName(''); setVarFormError(null);
  };

  const handleVarSave = async () => {
    if (!varName.trim()) { setVarFormError('שם הווריאציה הוא שדה חובה'); return; }
    const name = varName.trim();
    setVarSaving(true); setVarFormError(null);
    try {
      if (varEditing) {
        const { error } = await supabase.from('inventory_variation_types')
          .update({ name }).eq('id', varEditing.id);
        if (error) throw error;
        showToast('ווריאציה עודכנה בהצלחה');
      } else {
        const { error } = await supabase.from('inventory_variation_types')
          .insert({ company_id: activeCompanyId, name });
        if (error) throw error;
        showToast('ווריאציה נוספה בהצלחה');
      }
      closeVarModal(); fetchVariations();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'שגיאה';
      if (msg.toLowerCase().includes('unique')) setVarFormError('שם ווריאציה זה כבר קיים');
      else showToast(msg, false);
    } finally {
      setVarSaving(false);
    }
  };

  const handleVarDelete = async () => {
    if (!varDelTarget) return;
    setVarDeleting(true);
    const { error } = await supabase.from('inventory_variation_types').delete().eq('id', varDelTarget.id);
    if (error) { showToast(error.message, false); }
    else       { showToast('ווריאציה נמחקה'); setVarDelTarget(null); fetchVariations(); }
    setVarDeleting(false);
  };

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (!activeCompanyId) {
    return (
      <div className="p-6 flex items-center justify-center text-gray-400" dir="rtl">
        <Loader2 className="w-5 h-5 animate-spin ml-2" />
        <span>טוען חברה…</span>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-5" dir="rtl">

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-orange-100 rounded-xl">
          <Settings className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">הגדרות מלאי</h1>
          <p className="text-sm text-gray-500">
            ניהול מוצרים, לקוחות, ספקים ותצורת מלאי
          </p>
        </div>
      </div>

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      {toast && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium shadow
          ${toast.ok
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50   text-red-700   border border-red-200'}`}>
          {toast.ok
            ? <CheckCircle2 className="w-4 h-4 shrink-0" />
            : <AlertCircle  className="w-4 h-4 shrink-0" />}
          {toast.msg}
        </div>
      )}

      {/* ── Two-tier layout ───────────────────────────────────────────────── */}
      <div className="flex gap-5 items-start">

        {/* ── LEFT: Vertical category sidebar ─────────────────────────────
            (rendered on the right in RTL, visually the "left sidebar") */}
        <nav className="w-52 shrink-0 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">קטגוריות</p>
          </div>
          <ul className="py-2">
            {NAV.map(cat => {
              const isActive = activeMain === cat.id;
              return (
                <li key={cat.id}>
                  <button
                    onClick={() => switchMain(cat)}
                    className={`w-full flex items-center justify-between gap-2.5 px-4 py-2.5
                                text-sm font-medium transition-all rounded-none
                                ${isActive
                                  ? 'bg-orange-50 text-orange-700 border-r-2 border-orange-500'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                  >
                    <span className="flex items-center gap-2.5">
                      <span className={isActive ? 'text-orange-600' : 'text-gray-400'}>
                        {cat.icon}
                      </span>
                      {cat.label}
                    </span>
                    <ChevronLeft
                      className={`w-3.5 h-3.5 transition-transform
                        ${isActive ? 'text-orange-500 rotate-180' : 'text-gray-300'}`}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* ── RIGHT: Content pane ─────────────────────────────────────────── */}
        <div className="flex-1 min-w-0 space-y-4">

          {/* Sub-tab bar — hidden for single-view categories */}
          {!isSingleView && currentCat.subTabs.length > 1 && (
            <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-100
                            shadow-sm px-2 py-2 overflow-x-auto">
              {currentCat.subTabs.map(sub => {
                const isActive = activeSub === sub.id;
                return (
                  <button
                    key={sub.id}
                    onClick={() => setActiveSub(sub.id)}
                    className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm
                                font-medium whitespace-nowrap transition-all
                                ${isActive
                                  ? 'bg-orange-600 text-white shadow-sm'
                                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
                  >
                    <span className={isActive ? 'text-orange-100' : 'text-gray-400'}>
                      {sub.icon}
                    </span>
                    {sub.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* ── Section content ─────────────────────────────────────────── */}

          {/* PRODUCTS › Product Groups */}
          {activeSub === 'groups' && (
            <ProductGroupsSettings
              companyId={activeCompanyId}
              showToast={showToast}
            />
          )}

          {/* PRODUCTS › Pricelists */}
          {activeSub === 'pricelists' && (
            <PricelistsSettings
              companyId={activeCompanyId}
              showToast={showToast}
            />
          )}

          {/* PRODUCTS › Variations */}
          {activeSub === 'variations' && (
            <VariationsSection
              variations={variations}
              loading={varLoading}
              error={varError}
              onAdd={openVarAdd}
              onEdit={openVarEdit}
              onDelete={setVarDelTarget}
            />
          )}

          {/* PRODUCTS › SKU Settings */}
          {activeSub === 'sku' && (
            <SkuSettingsTab
              companyId={activeCompanyId}
              showToast={showToast}
            />
          )}

          {/* CLIENTS › Client Management */}
          {activeMain === 'clients' && (
            <ClientsSection
              clients={clients}
              loading={custLoading}
              error={custError}
              onAdd={openCustAdd}
              onEdit={openCustEdit}
              onDelete={setCustDelTarget}
              onInvite={handleInviteClient}
            />
          )}

          {/* AGENTS — top-level vertical nav category */}
          {activeMain === 'agents' && (
            <AgentsSettingsView
              companyId={activeCompanyId}
              showToast={showToast}
            />
          )}

          {/* SUPPLIERS › full CRUD */}
          {activeMain === 'suppliers' && (
            <SuppliersSettingsView
              companyId={activeCompanyId}
              showToast={showToast}
            />
          )}

          {/* WAREHOUSES — full CRUD */}
          {activeMain === 'warehouses' && (
            <WarehousesSettingsView
              companyId={activeCompanyId}
              showToast={showToast}
              companyConsignmentEnabled={companyConsignmentEnabled}
            />
          )}

          {/* COMPANY SETTINGS — VAT rate, global financial defaults */}
          {activeMain === 'company' && (
            <CompanySettingsView
              companyId={activeCompanyId}
              showToast={showToast}
            />
          )}

          {/* DOCUMENT TYPES — Phase 17 */}
          {activeMain === 'documents' && (
            <DocumentTypesSettingsView
              companyId={activeCompanyId}
              showToast={showToast}
            />
          )}

        </div>{/* end content pane */}
      </div>{/* end two-tier layout */}

      {/* ════════════════════════════════════════════════════════════════════
          MODALS
          ════════════════════════════════════════════════════════════════════ */}

      {/* Client Add/Edit — two-tab modal */}
      {custModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">

            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h3 className="text-lg font-bold text-gray-900">
                {custEditing ? 'עריכת לקוח' : 'הוספת לקוח חדש'}
              </h3>
              <button onClick={requestCloseCustModal}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 px-6 pt-4 shrink-0">
              {((custEditing ? ['general', 'extra', 'terms'] : ['general', 'extra']) as ('general' | 'extra' | 'terms')[]).map(tab => (
                <button
                  key={tab}
                  onClick={() => setCustModalTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                    ${custModalTab === tab
                      ? 'bg-orange-600 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
                >
                  {tab === 'general' ? 'פרטים כלליים' : tab === 'extra' ? 'הגדרות נוספות' : 'תנאים וכללים'}
                </button>
              ))}
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

              {custFormError && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200
                                rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />{custFormError}
                </div>
              )}

              {/* ── TAB: פרטים כלליים ─────────────────────────────────── */}
              {custModalTab === 'general' && (
                <>
                  {/* Name */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      שם הלקוח / החנות <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text" value={custName}
                      onChange={e => setCustName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCustSave()}
                      placeholder="לדוגמה: רשת סופר-פארם סניף הרצליה"
                      autoFocus
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                  </div>

                  {/* Contact hint */}
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                    יש למלא לפחות <strong>אימייל</strong> או <strong>טלפון</strong> — אחד מהם נדרש לכניסה דרך OTP.
                  </p>

                  {/* Email */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      אימייל
                      <span className="mr-1 text-xs font-normal text-gray-400">(אימייל או טלפון — לפחות אחד)</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="email" value={custEmail}
                        onChange={e => setCustEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCustSave()}
                        placeholder="store@example.com" dir="ltr"
                        className="w-full pr-9 pl-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                   focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      />
                    </div>
                    <p className="text-xs text-gray-400">ישמש למילוי אוטומטי בשליחת תעודות ולכניסה באפליקציה</p>
                  </div>

                  {/* Phone — Phase 72: strictly required when B2B portal is on */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      טלפון
                      {custB2bAllowed && <span className="text-red-500">*</span>}
                      {custB2bAllowed && (
                        <span className="text-xs font-normal text-red-500">
                          (חובה כאשר פורטל B2B פעיל)
                        </span>
                      )}
                    </label>
                    <div className="relative">
                      <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="tel" value={custPhone}
                        onChange={e => setCustPhone(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleCustSave()}
                        placeholder="050-0000000" dir="ltr"
                        className={`w-full pr-9 pl-3 py-2.5 border rounded-lg text-sm
                                   focus:outline-none focus:ring-2 focus:border-transparent
                                   ${custB2bAllowed && !custPhone.trim()
                                     ? 'border-red-300 focus:ring-red-400 bg-red-50/40'
                                     : 'border-gray-300 focus:ring-orange-400'}`}
                      />
                    </div>
                    <p className="text-xs text-gray-400">ישמש לכניסה ב-OTP טלפוני באפליקציית ההזמנות</p>
                  </div>

                  {/* Pricelist */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <Receipt className="w-3.5 h-3.5 text-violet-500" />
                      מחירון (לא חובה)
                    </label>
                    <div className="relative">
                      <select
                        value={custPricelistId ?? ''}
                        onChange={e => setCustPricelistId(e.target.value || null)}
                        className="w-full appearance-none px-3 py-2.5 border border-gray-300 rounded-lg
                                   text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-400
                                   focus:border-transparent pr-3 pl-8"
                      >
                        <option value="">— ללא מחירון —</option>
                        {pricelists.map(pl => (
                          <option key={pl.id} value={pl.id}>{pl.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4
                                              text-gray-400 pointer-events-none" />
                    </div>
                    {pricelists.length === 0 && (
                      <p className="text-xs text-violet-600">הגדר מחירונים תחת מוצרים ומלאי → מחירונים</p>
                    )}
                  </div>

                  {/* Agent */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5 text-teal-500" />
                      סוכן מטפל (לא חובה)
                    </label>
                    <div className="relative">
                      <select
                        value={custAgentId ?? ''}
                        onChange={e => setCustAgentId(e.target.value || null)}
                        className="w-full appearance-none px-3 py-2.5 border border-gray-300 rounded-lg
                                   text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-400
                                   focus:border-transparent pr-3 pl-8"
                      >
                        <option value="">— ללא סוכן —</option>
                        {agents.map(ag => (
                          <option key={ag.id} value={ag.id}>{ag.name}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4
                                              text-gray-400 pointer-events-none" />
                    </div>
                    {agents.length === 0 && (
                      <p className="text-xs text-teal-600">הוסף סוכנים תחת הגדרות → סוכנים</p>
                    )}
                  </div>
                </>
              )}

              {/* ── TAB: הגדרות נוספות ──────────────────────────────────── */}
              {custModalTab === 'extra' && (
                <>
                  {/* Business ID — ח.פ. / עוסק מורשה */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-indigo-500" />
                      ח.פ. / עוסק מורשה
                      <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
                    </label>
                    <input
                      type="text"
                      value={custBusinessId}
                      onChange={e => setCustBusinessId(e.target.value)}
                      placeholder="לדוגמה: 514123456"
                      dir="ltr"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-400">
                      מספר ח.פ. משמש לקיבוץ סניפים שונים תחת אותו עסק בפורטל ההזמנות
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-sky-500" />
                      מספר לקוח ברווחית
                      <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={custRivhitId}
                        onChange={e => setCustRivhitId(e.target.value.replace(/\D/g, ''))}
                        placeholder="יקושר אוטומטית בהפקה הראשונה"
                        dir="ltr"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                   focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
                      />
                      {custRivhitId && (
                        <button type="button" onClick={() => setCustRivhitId('')}
                                className="px-3 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shrink-0">
                          נקה קישור
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      לכרטיס הזה ברווחית יופקו חשבוניות הלקוח. השאירו ריק כדי שהמערכת תזהה לפי ח.פ או תשאל אתכם בהפקה.
                    </p>
                  </div>

                  {/* B2B Portal Access toggle */}
                  <div
                    className={`flex items-center justify-between px-4 py-3.5 rounded-xl border
                                transition-colors cursor-pointer
                                ${custB2bAllowed
                                  ? 'bg-green-50 border-green-300'
                                  : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setCustB2bAllowed(v => !v)}
                    role="checkbox"
                    aria-checked={custB2bAllowed}
                    tabIndex={0}
                    onKeyDown={e => e.key === ' ' && setCustB2bAllowed(v => !v)}
                  >
                    <div>
                      <p className={`text-sm font-semibold
                                     ${custB2bAllowed ? 'text-green-800' : 'text-gray-700'}`}>
                        אפשר גישה לפורטל הזמנות B2B
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        הלקוח משויך למערכת B2B
                      </p>
                      {custB2bAllowed && !custPhone.trim() && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3 shrink-0" />
                          יש להזין מספר טלפון כדי לאפשר גישה לפורטל B2B.
                        </p>
                      )}
                    </div>
                    {custB2bAllowed
                      ? <ToggleRight className="w-8 h-8 text-green-500 shrink-0" />
                      : <ToggleLeft  className="w-8 h-8 text-gray-300 shrink-0" />
                    }
                  </div>

                  {/* Phase 109: Client access level selector */}
                  {custB2bAllowed && (
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-gray-700">רמת גישה ללקוח</label>
                      <select
                        value={custClientAccess}
                        onChange={e => setCustClientAccess(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                   focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                      >
                        <option value="none">סוכן בלבד — הלקוח לא יכול להתחבר</option>
                        <option value="view_only">צפייה בלבד — הלקוח רואה קטלוג אבל לא מזמין</option>
                        <option value="full">גישה מלאה — הלקוח מתחבר ומזמין בעצמו</option>
                      </select>
                      <p className="text-xs text-gray-400">
                        {custClientAccess === 'none' && 'רק הסוכן יוכל להזמין עבור לקוח זה'}
                        {custClientAccess === 'view_only' && 'הלקוח יוכל לצפות בקטלוג אבל לא להגיש הזמנות'}
                        {custClientAccess === 'full' && 'הלקוח יוכל להתחבר לפורטל ולהגיש הזמנות בעצמו'}
                      </p>
                    </div>
                  )}

                  {/* VAT liable toggle */}
                  <div
                    className={`flex items-center justify-between px-4 py-3.5 rounded-xl border
                                transition-colors cursor-pointer
                                ${custIsVatLiable
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
                    onClick={() => setCustIsVatLiable(v => !v)}
                    role="checkbox"
                    aria-checked={custIsVatLiable}
                    tabIndex={0}
                    onKeyDown={e => e.key === ' ' && setCustIsVatLiable(v => !v)}
                  >
                    <div>
                      <p className={`text-sm font-semibold
                                     ${custIsVatLiable ? 'text-blue-800' : 'text-gray-700'}`}>
                        לקוח חייב במע״מ
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        כבה רק עבור לקוחות הפטורים ממע״מ (עמותות, ייצוא וכו׳)
                      </p>
                    </div>
                    {custIsVatLiable
                      ? <ToggleRight className="w-8 h-8 text-blue-500 shrink-0" />
                      : <ToggleLeft  className="w-8 h-8 text-gray-300 shrink-0" />
                    }
                  </div>

                  {/* ── Phase 72/78: הגדרת קונסגנציה (Consignment Setup) ──── */}
                  <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 space-y-4">
                    <div className="flex items-center gap-2">
                      <Warehouse className="w-4 h-4 text-amber-600" />
                      <h4 className="text-sm font-semibold text-amber-900">הגדרת קונסגנציה</h4>
                    </div>

                    {/* Phase 78: block if consignment not enabled at company level */}
                    {!companyConsignmentEnabled && (
                      <div className="flex items-start gap-2 px-4 py-3 bg-gray-100 border border-gray-300 rounded-xl text-sm text-gray-600">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                        <span>
                          מכירה בקונסגנציה לא מופעלת לחברה זו.
                          כדי להגדיר לקוח קונסגנציה, יש להפעיל תחילה את{' '}
                          <strong>הגדרות חברה → הגדרות קונסגנציה</strong>.
                        </span>
                      </div>
                    )}

                    {/* Consignment Warehouse dropdown — disabled when company flag is off */}
                    <div className={`space-y-4 ${!companyConsignmentEnabled ? 'opacity-40 pointer-events-none' : ''}`}>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                        מחסן קונסגנציה
                        <span className="text-xs font-normal text-gray-400">(אופציונלי)</span>
                      </label>
                      <div className="relative">
                        <select
                          value={custConsignmentWHId ?? ''}
                          onChange={e => handleConsignmentWHChange(e.target.value || null)}
                          className="w-full appearance-none px-3 py-2.5 border border-gray-300 rounded-lg
                                     text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400
                                     focus:border-transparent pr-3 pl-8"
                        >
                          <option value="">— ללא מחסן קונסגנציה —</option>
                          {consignmentWarehouses.map(wh => (
                            <option key={wh.id} value={wh.id}>
                              {wh.warehouse_number ? `[${wh.warehouse_number}] ` : ''}{wh.name}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4
                                                text-gray-400 pointer-events-none" />
                      </div>
                      {consignmentWarehouses.length === 0 && (
                        <p className="text-xs text-amber-700">
                          אין מחסני קונסגנציה — הגדר מחסן מסוג קונסגנציה תחת הגדרות → מחסנים
                        </p>
                      )}
                      {custConsignmentWHId && consignmentWarehouses.length > 0 && (
                        <p className="text-xs text-gray-500">
                          המחסן המשויך ישמש לתעודות ולמלאי קונסגנציה אצל הלקוח.
                        </p>
                      )}
                    </div>

                    {/* Allow Consignment Sales toggle (strict interlock) */}
                    <div
                      className={`flex items-center justify-between px-4 py-3.5 rounded-xl border
                                  transition-colors cursor-pointer
                                  ${custAllowConsignment
                                    ? 'bg-amber-100 border-amber-400'
                                    : !custConsignmentWHId
                                      ? 'bg-white border-gray-200 opacity-90 hover:border-amber-300'
                                      : 'bg-white border-gray-200 hover:border-amber-300'}`}
                      onClick={tryToggleAllowConsignment}
                      role="checkbox"
                      aria-checked={custAllowConsignment}
                      aria-disabled={!custConsignmentWHId && !custAllowConsignment}
                      tabIndex={0}
                      onKeyDown={e => e.key === ' ' && tryToggleAllowConsignment()}
                    >
                      <div className="min-w-0 pr-2">
                        <p className={`text-sm font-semibold
                                       ${custAllowConsignment ? 'text-amber-900' : 'text-gray-700'}`}>
                          לאפשר מכירה בקונסגנציה
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          כאשר מופעל — ניתן יהיה להפיק תעודות מכירה בקונסגנציה ללקוח זה.
                        </p>
                      </div>
                      {custAllowConsignment
                        ? <ToggleRight className="w-8 h-8 text-amber-600 shrink-0" />
                        : <ToggleLeft  className="w-8 h-8 text-gray-300 shrink-0" />
                      }
                    </div>

                    {/* Allow Standard Sales toggle */}
                    <div
                      className={`flex items-center justify-between px-4 py-3.5 rounded-xl border
                                  transition-colors cursor-pointer
                                  ${custAllowStandardSales
                                    ? 'bg-blue-50 border-blue-300'
                                    : 'bg-white border-gray-200 hover:border-blue-300'}`}
                      onClick={() => setCustAllowStandardSales(v => !v)}
                      role="checkbox"
                      aria-checked={custAllowStandardSales}
                      tabIndex={0}
                      onKeyDown={e => e.key === ' ' && setCustAllowStandardSales(v => !v)}
                    >
                      <div className="min-w-0 pr-2">
                        <p className={`text-sm font-semibold
                                       ${custAllowStandardSales ? 'text-blue-900' : 'text-gray-700'}`}>
                          לאפשר מכירה רגילה (סטנדרט)
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          כאשר מופעל — ניתן יהיה לשדר הזמנות רגילות (לא קונסגנציה) ללקוח זה.
                        </p>
                      </div>
                      {custAllowStandardSales
                        ? <ToggleRight className="w-8 h-8 text-blue-600 shrink-0" />
                        : <ToggleLeft  className="w-8 h-8 text-gray-300 shrink-0" />
                      }
                    </div>

                    {/* Mode summary label */}
                    {(custAllowConsignment || custAllowStandardSales) && (
                      <div className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                        <p className="text-xs font-semibold text-gray-700">
                          מצב פעולה:{' '}
                          {custAllowConsignment && custAllowStandardSales
                            ? <span className="text-purple-700">מצב כפול — הסוכן יבחר בכל הזמנה</span>
                            : custAllowConsignment
                              ? <span className="text-amber-700">קונסגנציה בלבד</span>
                              : <span className="text-blue-700">מכירה רגילה בלבד</span>
                          }
                        </p>
                      </div>
                    )}

                    {/* Interlock warning (transient, until user sets a warehouse) */}
                    {custConsignmentWarning && (
                      <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-50
                                      border border-red-200 text-xs text-red-700">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{custConsignmentWarning}</span>
                      </div>
                    )}
                    </div>{/* end consignment-disabled wrapper */}
                  </div>
                </>
              )}

              {custEditing && activeCompanyId && (
                <div className={custModalTab === 'terms' ? '' : 'hidden'}>
                  <B2BClientTermsPanel ref={custTermsRef} clientId={custEditing.id} companyId={activeCompanyId} />
                </div>
              )}

            </div>{/* end scrollable body */}

            {/* Modal footer */}
            <div className="flex items-center gap-3 justify-end px-6 py-4 border-t border-gray-100 shrink-0">
              <button onClick={requestCloseCustModal}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                                 border border-gray-200 rounded-lg transition-colors">
                ביטול
              </button>
              <button
                onClick={handleCustSave}
                disabled={
                  custSaving
                  || !custName.trim()
                  /* Phase 72: B2B portal access requires phone */
                  || (custB2bAllowed && !custPhone.trim())
                  /* Phase 72: consignment toggle requires a warehouse */
                  || (custAllowConsignment && !custConsignmentWHId)
                }
                className="flex items-center gap-2 px-5 py-2 bg-orange-600 hover:bg-orange-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {custSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {custEditing ? 'שמור שינויים' : 'הוסף לקוח'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Delete Confirm */}
      {custDelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת לקוח</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את{' '}
                  <span className="font-semibold text-gray-800">{custDelTarget.name}</span>?
                  {' '}פעולה זו תמחק גם את כל התעודות הקשורות. אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button onClick={() => setCustDelTarget(null)}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                                 border border-gray-200 rounded-lg transition-colors">
                ביטול
              </button>
              <button onClick={handleCustDelete} disabled={custDeleting}
                      className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                                 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                {custDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variation Add/Edit */}
      {varModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {varEditing ? 'עריכת ווריאציה' : 'הוספת ווריאציה חדשה'}
              </h3>
              <button onClick={closeVarModal}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {varFormError && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200
                              rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />{varFormError}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                שם ציר הווריאציה <span className="text-red-500">*</span>
              </label>
              <input
                type="text" value={varName}
                onChange={e => setVarName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleVarSave()}
                placeholder="לדוגמה: מידה, צבע, גודל, חומר..."
                autoFocus
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
              />
              <p className="text-xs text-gray-400">שם ייחודי לכל חברה — ישמש בהגדרת פריטי מלאי.</p>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <button onClick={closeVarModal}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                                 border border-gray-200 rounded-lg transition-colors">
                ביטול
              </button>
              <button
                onClick={handleVarSave}
                disabled={varSaving || !varName.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {varSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {varEditing ? 'שמור שינויים' : 'הוסף ווריאציה'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variation Delete Confirm */}
      {varDelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת ווריאציה</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את הווריאציה{' '}
                  <span className="font-semibold text-gray-800">"{varDelTarget.name}"</span>?
                  {' '}פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button onClick={() => setVarDelTarget(null)}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                                 border border-gray-200 rounded-lg transition-colors">
                ביטול
              </button>
              <button onClick={handleVarDelete} disabled={varDeleting}
                      className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                                 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                {varDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pure-render section components (no hooks → no rules-of-hooks issues)
// ─────────────────────────────────────────────────────────────────────────────

// ── Clients section ───────────────────────────────────────────────────────────

interface ClientsSectionProps {
  clients:  InventoryClient[];
  loading:  boolean;
  error:    string | null;
  onAdd:    () => void;
  onEdit:   (c: InventoryClient) => void;
  onDelete: (c: InventoryClient) => void;
  onInvite: (c: InventoryClient) => void;
}


function ClientsSection({ clients, loading, error, onAdd, onEdit, onDelete, onInvite }: ClientsSectionProps) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const filteredClients = clients.filter(c => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.trim().toLowerCase();
    return (c.name?.toLowerCase().includes(q)) ||
           (c.email?.toLowerCase().includes(q)) ||
           (c.phone?.includes(q)) ||
           (c.business_id?.includes(q));
  });
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <Store className="w-5 h-5 text-orange-500" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">ניהול לקוחות</h2>
            <p className="text-xs text-gray-500">חנויות / נקודות מכירה המקבלות סחורה</p>
          </div>
        </div>
        <button
          onClick={onAdd}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700
                     text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          הוסף לקוח
        </button>
      </div>

      {!loading && clients.length > 0 && (
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="חיפוש לפי שם, אימייל, טלפון או ח.פ..."
              className="w-full pr-10 pl-4 py-2 text-sm border border-gray-200 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400
                         placeholder:text-gray-400 transition-colors"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 mx-6 my-4 px-4 py-3 bg-red-50 border border-red-200
                        rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">טוען לקוחות…</span>
        </div>
      ) : clients.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400">
          <Store className="w-10 h-10 text-gray-300" />
          <p className="text-sm font-medium">אין לקוחות עדיין</p>
          <p className="text-xs text-gray-400">לחץ על "הוסף לקוח" כדי להוסיף חנות ראשונה</p>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold
                           text-gray-500 uppercase tracking-wide">
              <th className="px-6 py-3 text-right">שם הלקוח</th>
              <th className="px-6 py-3 text-right">אימייל</th>
              <th className="px-6 py-3 text-right">טלפון</th>
              <th className="px-6 py-3 text-right">מחירון</th>
              <th className="px-6 py-3 text-right">סוכן</th>
              <th className="px-6 py-3 text-right w-36">פעולות</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredClients.map(c => (
              <tr key={c.id} className="hover:bg-gray-50/60 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium text-gray-900">{c.name}</span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {c.business_id && (
                        <span className="text-[10px] text-gray-400 font-mono" dir="ltr">
                          ח.פ. {c.business_id}
                        </span>
                      )}
                      {c.b2b_allowed && (
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                          c.client_access === 'full' ? 'bg-green-50 text-green-700'
                          : c.client_access === 'view_only' ? 'bg-amber-50 text-amber-700'
                          : 'bg-gray-100 text-gray-500'
                        }`}>
                          {c.client_access === 'full' ? 'B2B מלא' : c.client_access === 'view_only' ? 'צפייה בלבד' : 'סוכן בלבד'}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-500">
                  {c.email ? (
                    <a href={`mailto:${c.email}`}
                       className="flex items-center gap-1.5 hover:text-blue-600 transition-colors w-fit">
                      <Mail className="w-3.5 h-3.5 shrink-0" />{c.email}
                    </a>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-6 py-4 text-gray-500">
                  {c.phone ? (
                    /* Plain text — no tel: link to avoid accidental phone-call
                       navigation when the Invite button is nearby             */
                    <span className="flex items-center gap-1.5 select-all" dir="ltr">
                      <Phone className="w-3.5 h-3.5 shrink-0 text-gray-400" />{c.phone}
                    </span>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-6 py-4">
                  {c.pricelist_name ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5
                                     bg-violet-50 text-violet-700 text-xs font-medium rounded-full">
                      <Receipt className="w-3 h-3" />
                      {c.pricelist_name}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400 italic">מחיר בסיס</span>
                  )}
                </td>
                {/* Agent column */}
                <td className="px-6 py-4">
                  {c.agent_name ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5
                                     bg-teal-50 text-teal-700 text-xs font-medium rounded-full">
                      <Briefcase className="w-3 h-3" />
                      {c.agent_name}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    {/* Invite — WhatsApp if phone, mailto if email-only, toast if neither */}
                    <button
                      onClick={() => onInvite(c)}
                      className={[
                        'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors',
                        c.phone
                          ? 'text-green-700 bg-green-50 hover:bg-green-100'
                          : c.email
                            ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                            : 'text-gray-400 bg-gray-50 hover:bg-gray-100',
                      ].join(' ')}
                      title={c.phone ? 'שלח הזמנה ב-WhatsApp' : c.email ? 'שלח הזמנה במייל' : 'אין פרטי קשר לשליחה'}
                    >
                      <SendHorizonal className="w-3.5 h-3.5" />
                      הזמן
                    </button>

                    {/* Client ledger */}
                    <button
                      onClick={() => navigate(`/app/inventory/clients/${c.id}/ledger`)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs
                                 font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
                      title="כרטסת לקוח"
                    >
                      <ReceiptText className="w-3.5 h-3.5" />
                      כרטסת
                    </button>

                    <button onClick={() => onEdit(c)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                       rounded-lg transition-colors" title="ערוך">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(c)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50
                                       rounded-lg transition-colors" title="מחק">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {clients.length > 0 && (
        <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400">
          {clients.length} לקוחות · שייך מחירון ללקוח כדי להחיל הנחות אוטומטיות
        </div>
      )}
    </section>
  );
}

// ── Variations section ────────────────────────────────────────────────────────

interface VariationsSectionProps {
  variations: InventoryVariationType[];
  loading:    boolean;
  error:      string | null;
  onAdd:      () => void;
  onEdit:     (v: InventoryVariationType) => void;
  onDelete:   (v: InventoryVariationType) => void;
}

function VariationsSection({ variations, loading, error, onAdd, onEdit, onDelete }: VariationsSectionProps) {

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <Sliders className="w-5 h-5 text-indigo-500" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">הגדרת וריאציות</h2>
            <p className="text-xs text-gray-500">ציר וריאציה לפריטי מלאי — מידה, צבע, גודל…</p>
          </div>
        </div>
        <button
          onClick={onAdd}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
                     text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          הוסף ווריאציה
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-3 mx-6 my-4 px-4 py-3 bg-red-50 border border-red-200
                        rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">טוען וריאציות…</span>
        </div>
      ) : variations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400">
          <Sliders className="w-10 h-10 text-gray-300" />
          <p className="text-sm font-medium">אין ווריאציות מוגדרות</p>
          <p className="text-xs text-gray-400">לחץ על "הוסף ווריאציה" כדי להגדיר את הראשונה</p>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold
                           text-gray-500 uppercase tracking-wide">
              <th className="px-6 py-3 text-right">שם הווריאציה</th>
              <th className="px-6 py-3 text-right w-28">פעולות</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {variations.map(v => (
              <tr key={v.id} className="hover:bg-gray-50/60 transition-colors">
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1
                                   bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">
                    <Sliders className="w-3 h-3" />{v.name}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button onClick={() => onEdit(v)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                       rounded-lg transition-colors" title="ערוך">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(v)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50
                                       rounded-lg transition-colors" title="מחק">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {variations.length > 0 && (
        <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400">
          {variations.length} ווריאציות · ניתן להשתמש בהן בעת הגדרת פריטי מלאי
        </div>
      )}
    </section>
  );
}

